// exportApi.js - video export: start, live progress (polling), cancel, delete.
// Replaces the old Firebase progress. Progress comes from the API (SQL Server), polled every 2 s
// while something is exporting.
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { postFormField } from "./compressedUpload";

// Where finished videos are downloaded from. The render worker writes them to the shared Renders
// folder, which this host serves.
export const VIDEO_BASE_URL = "https://videopreview.net/AnimatricksAPI";

export const ACTIVE_STATES = ["Queued", "Rendering", "Encoding"];

export function videoUrl(item) {
  if (!item || !item.videoPath) return null;
  return VIDEO_BASE_URL.replace(/\/$/, "") + "/" + encodeURI(item.videoPath.replace(/^\//, ""));
}

function authHeaders() {
  try {
    const auth = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return auth && auth.access_token ? { Authorization: "Bearer " + auth.access_token } : {};
  } catch {
    return {};
  }
}

function url(apiBaseURL, path) {
  return String(apiBaseURL).replace(/\/$/, "") + "/api/Export/" + path;
}

/** Queues an export. Returns { status, renderId, format } or { status: false, error }. */
export async function startExport(apiBaseURL, exportObj) {
  // Compressed when large (Utils/compressedUpload.js); the API unpacks it before reading the form.
  const res = await postFormField(url(apiBaseURL, "CallForExport"), "value", JSON.stringify(exportObj), {
    headers: authHeaders(),
  });
  return res.data || { status: false };
}

/** The signed-in user's exports, newest first. */
export async function listMyExports(apiBaseURL, top = 50) {
  const res = await axios.get(url(apiBaseURL, "MyExports?top=" + top), { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
}

export async function cancelExport(apiBaseURL, id) {
  const res = await axios.post(url(apiBaseURL, "Cancel/" + id), null, { headers: authHeaders() });
  return res.data;
}

/** Deletes the video and its record (a running export is stopped first). */
export async function deleteExport(apiBaseURL, id) {
  const res = await axios.get(url(apiBaseURL, "DeletVideo/" + id), { headers: authHeaders() });
  return res.data;
}

/** Human-readable status for one export. */
export function describeExport(item) {
  if (!item) return "";
  switch (item.state) {
    case "Queued":
      return item.queuePosition > 0 ? `Waiting in queue (${item.queuePosition} ahead)` : "Waiting in queue";
    case "Rendering":
      return item.cancelRequested ? "Cancelling…" : "Rendering";
    case "Encoding":
      return item.cancelRequested ? "Cancelling…" : "Adding audio and finishing";
    case "Completed":
      return "Completed";
    case "Cancelled":
      return "Cancelled";
    case "Failed":
      return "Failed";
    default:
      return item.stage || item.state || "";
  }
}

/**
 * Live list of the user's exports. Polls every 2 s while any export is queued/running
 * (10 s while the browser tab is hidden) and stops when everything has finished.
 *   const { items, loading, error, refresh, cancel, remove } = useMyExports(apiBaseURL, open);
 */
export function useMyExports(apiBaseURL, enabled = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const generation = useRef(0);

  const stop = useCallback(() => {
    generation.current += 1;
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const refresh = useCallback(async () => {
    stop();
    const gen = generation.current;
    let failures = 0;
    setLoading(true);

    const tick = async () => {
      let next = null;
      try {
        const list = await listMyExports(apiBaseURL);
        if (gen !== generation.current) return;
        failures = 0;
        setError(null);
        setItems(list);
        if (list.some((x) => ACTIVE_STATES.includes(x.state))) {
          next = document.hidden ? 10000 : 2000;
        }
      } catch (e) {
        if (gen !== generation.current) return;
        const status = e && e.response && e.response.status;
        if (status === 401 || status === 403) {
          setError("Your session has expired. Please sign in again.");
        } else {
          failures += 1;
          setError("Could not load your videos. Retrying…");
          next = Math.min(15000, 2000 * 2 ** Math.min(failures, 3));
        }
      } finally {
        if (gen === generation.current) setLoading(false);
      }
      if (next !== null && gen === generation.current) timer.current = setTimeout(tick, next);
    };

    await tick();
  }, [apiBaseURL, stop]);

  useEffect(() => {
    if (enabled) refresh();
    else stop();
    return stop;
  }, [enabled, refresh, stop]);

  const cancel = useCallback(
    async (id) => {
      await cancelExport(apiBaseURL, id);
      await refresh();
    },
    [apiBaseURL, refresh]
  );

  const remove = useCallback(
    async (id) => {
      await deleteExport(apiBaseURL, id);
      await refresh();
    },
    [apiBaseURL, refresh]
  );

  return { items, loading, error, refresh, cancel, remove };
}
